import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Star, Loader2, Trash2, ClipboardEdit, Clock, X } from 'lucide-react';

const VEHICLE_LABEL = { bm: 'Boîte manuelle', ba: 'Boîte auto', simulateur: 'Simulateur' };
const LESSON_STATUS = { planifie: ['Planifiée', 'text-white/50'], effectue: ['Effectuée ✓', 'text-emerald-400'], absent_excuse: ['Abs. excusée', 'text-orange-300'], absent_non_excuse: ['Abs. non excusée', 'text-red-400'] };

export const Stars = ({ value, onChange, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange && onChange(n)} data-testid={onChange ? `bilan-star-${n}` : undefined} className={onChange ? 'cursor-pointer' : 'cursor-default'}>
        <Star size={size} className={n <= value ? 'text-amber-400 fill-amber-400' : 'text-white/15'} />
      </button>
    ))}
  </span>
);

export const RecentLessonsSection = ({ lessons }) => {
  if (!lessons || lessons.length === 0) return null;
  return (
    <div className="mb-8" data-testid="recent-lessons-section">
      <h2 className="font-serif-editorial text-white text-[22px] mb-1 flex items-center gap-2"><Clock size={17} className="text-red-400" /> Dernières leçons</h2>
      <p className="text-white/45 text-[13px] mb-4">Les dernières leçons du planning, avec les commentaires de chaque enseignant — même si un autre moniteur l'a eu.</p>
      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] divide-y divide-white/5">
        {lessons.map((l) => {
          const [stLabel, stCls] = LESSON_STATUS[l.status] || [l.status, 'text-white/50'];
          return (
            <div key={l.id} className="px-4 py-3" data-testid={`recent-lesson-${l.id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: l.teacher_color }} />
                <span className="font-mono-spec text-white/80 text-[12.5px]">{new Date(l.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} · {l.start_time}</span>
                <span className="text-white text-[13px] font-semibold">{l.teacher_name}</span>
                <span className="text-white/45 text-[11.5px] uppercase">{l.type} · {l.duration_min} min · {VEHICLE_LABEL[l.vehicle_type] || l.vehicle_type}</span>
                <span className={`ml-auto text-[11.5px] font-bold ${stCls}`}>{stLabel}</span>
              </div>
              {l.notes && <div className="mt-1.5 pl-5 text-sky-300/75 text-[12.5px]">💬 {l.notes}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EMPTY_FORM = { title: '', rating: 3, comment: '', points_forts: '', points_travailler: '' };

const BilanCard = ({ b, canDelete, onDelete }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5" data-testid={`bilan-card-${b.id}`}>
    <div className="flex flex-wrap items-center gap-3 mb-2">
      <span className="text-white text-[14.5px] font-semibold">{b.title}</span>
      <Stars value={b.rating} size={13} />
      <span className="ml-auto text-white/40 text-[11.5px] font-mono-spec">{new Date(b.created_at).toLocaleDateString('fr-FR')} · {b.teacher_name}</span>
      {canDelete && <button onClick={onDelete} data-testid={`bilan-delete-${b.id}`} className="text-red-400/60 hover:text-red-300 p-1"><Trash2 size={13} /></button>}
    </div>
    {b.comment && <p className="text-white/70 text-[13px] leading-relaxed whitespace-pre-wrap mb-2">{b.comment}</p>}
    <div className="grid sm:grid-cols-2 gap-2">
      {b.points_forts && <div className="rounded-lg bg-emerald-950/25 border border-emerald-700/25 p-2.5 text-[12.5px] text-emerald-200/85">✅ <b>Points forts :</b> {b.points_forts}</div>}
      {b.points_travailler && <div className="rounded-lg bg-orange-950/25 border border-orange-700/25 p-2.5 text-[12.5px] text-orange-200/85">🎯 <b>À travailler :</b> {b.points_travailler}</div>}
    </div>
  </div>
);

export const StudentBilansSection = ({ studentId }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    api.teacherStudentBilans(studentId).then((d) => setItems(d.items || [])).catch(() => {});
  }, [studentId]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    setSaving(true);
    try {
      await api.teacherCreateStudentBilan(studentId, { ...form, rating: Number(form.rating) });
      setForm(EMPTY_FORM); setShowForm(false); refresh();
    } catch (_) { window.alert('Enregistrement impossible — réessaie.'); }
    setSaving(false);
  };

  const remove = async (b) => {
    if (!window.confirm('Supprimer ce bilan ?')) return;
    await api.teacherDeleteStudentBilan(b.id).catch(() => {});
    refresh();
  };

  const inputCls = 'w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-white text-[13px] outline-none focus:border-red-500';

  return (
    <div className="mb-12" data-testid="student-bilans-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-serif-editorial text-white text-[24px] flex items-center gap-2"><ClipboardEdit size={18} className="text-red-400" /> Bilans pédagogiques</h2>
        <button onClick={() => setShowForm((v) => !v)} data-testid="bilan-toggle-form-btn" className={`h-9 px-4 rounded-full text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${showForm ? 'border border-white/20 text-white/70' : 'bg-red-600 hover:bg-red-500 text-white'}`}>
          {showForm ? <><X size={13} /> Annuler</> : '+ Ajouter un bilan'}
        </button>
      </div>
      <p className="text-white/45 text-[13px] mb-4">Note l'élève et laisse une trace écrite — visible par l'élève et les autres enseignants.</p>

      {showForm && (
        <div className="rounded-2xl border border-red-700/30 bg-red-950/10 p-5 mb-4 space-y-3" data-testid="bilan-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre du bilan (ex : Leçon 12 — circulation en ville) *" maxLength={150} data-testid="bilan-title-input" className={inputCls} style={{ height: 40 }} />
          <div className="flex items-center gap-3">
            <span className="text-white/55 text-[12.5px]">Note globale :</span>
            <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={20} />
            <span className="text-amber-300 text-[13px] font-bold">{form.rating}/5</span>
          </div>
          <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Commentaire général sur la séance / la période…" rows={3} maxLength={2000} data-testid="bilan-comment-input" className={inputCls} />
          <div className="grid sm:grid-cols-2 gap-2">
            <textarea value={form.points_forts} onChange={(e) => setForm({ ...form, points_forts: e.target.value })} placeholder="✅ Points forts" rows={2} maxLength={1000} data-testid="bilan-forts-input" className={inputCls} />
            <textarea value={form.points_travailler} onChange={(e) => setForm({ ...form, points_travailler: e.target.value })} placeholder="🎯 Points à travailler" rows={2} maxLength={1000} data-testid="bilan-travailler-input" className={inputCls} />
          </div>
          <button onClick={save} disabled={saving || form.title.trim().length < 2} data-testid="bilan-save-btn" className="h-10 px-6 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white text-[13px] font-bold inline-flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Enregistrer le bilan
          </button>
        </div>
      )}

      {items.length === 0 && !showForm ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-6 text-center text-white/45 text-[13px]" data-testid="bilans-empty">Aucun bilan pédagogique — ajoute le premier !</div>
      ) : (
        <div className="space-y-3">
          {items.map((b) => <BilanCard key={b.id} b={b} canDelete={user?.id === b.teacher_id} onDelete={() => remove(b)} />)}
        </div>
      )}
    </div>
  );
};

export const MaFormationCard = () => {
  const [f, setF] = useState(null);
  useEffect(() => { api.myFormation().then(setF).catch(() => {}); }, []);
  if (!f || !f.linked) return null;
  const pack = f.hours_package || 0;
  const remaining = pack ? Math.round((pack - f.hours_done) * 10) / 10 : null;
  const pct = pack ? Math.min(100, Math.round((f.hours_done / pack) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-sky-950/25 to-[#0d0d0e] p-5 mb-6" data-testid="ma-formation-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="text-[10.5px] uppercase tracking-[0.2em] font-mono-spec text-sky-300 font-bold">Ma formation</div>
        {f.formula_label && <span className="px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-bold">{f.formula_label}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {pack > 0 && (
          <div className="min-w-[180px]">
            <div className="text-white text-[15px] font-semibold">{f.hours_done} / {pack} h de conduite</div>
            <div className="h-1.5 max-w-[180px] bg-white/8 rounded-full overflow-hidden my-1.5">
              <div className={`h-full ${remaining < 0 ? 'bg-red-500' : 'bg-sky-400'}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="text-white/50 text-[12px]">{remaining < 0 ? `Forfait dépassé de ${Math.abs(remaining)} h` : `Il te reste ${remaining} h sur ton forfait`}</div>
          </div>
        )}
        {f.next_lesson ? (
          <div className="text-[13px]">
            <div className="text-white/45 text-[11px] uppercase tracking-wider mb-0.5">Prochaine leçon</div>
            <div className="text-white font-semibold">
              {new Date(f.next_lesson.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {f.next_lesson.start_time}
            </div>
            <div className="text-white/50 text-[12px]">{f.next_lesson.duration_min} min · {f.next_lesson.teacher_name}</div>
          </div>
        ) : (
          <div className="text-white/40 text-[12.5px]">Aucune leçon planifiée — rapproche-toi de ton auto-école.</div>
        )}
      </div>
    </div>
  );
};

export const MyBilansSection = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.myBilans().then((d) => setItems(d.items || [])).catch(() => setItems([])); }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-10" data-testid="my-bilans-section">
      <div className="text-white/60 text-[10.5px] sm:text-[11.5px] uppercase tracking-wider font-mono-spec inline-flex items-center gap-1.5 mb-3"><ClipboardEdit size={11} /> Mes bilans pédagogiques</div>
      <div className="space-y-3">
        {items.map((b) => <BilanCard key={b.id} b={b} canDelete={false} onDelete={() => {}} />)}
      </div>
    </div>
  );
};
